import { getIn } from 'formik';
import { useCallback, useMemo } from 'react';
import Select, { PropsValue, Props as ReactSeletProps } from 'react-select';
import cn from 'classnames';
import styles from './DropDownControl.module.css';

interface IGroupedOptions<TDataItem> {
    label: string;
    options: TDataItem[];
}

type ChangeHandler<T> = (item: T) => void;

const MENU_PORTAL_ZINDEX = 1200; // This should match --zindex-above-bootstrap in src/app/index.css

export type IDropDownControlProps<TDataItem, TValue = undefined | string | string[] | TDataItem | TDataItem[]> = Omit<
    ReactSeletProps<TDataItem, boolean>,
    | 'options'
    | 'value'
    | 'isDisabled'
    | 'isMulti'
    | 'isSearchable'
    | 'closeMenuOnSelect'
    | 'menuPortalTarget'
    | 'onChange'
    | 'onInputChange'
> & {
    data: TDataItem[] | IGroupedOptions<TDataItem>[];
    textField?: string;
    valueField?: string;
    value: TDataItem | TDataItem[] | TValue | TValue[];
    disabled?: boolean;

    multiple?: boolean;
    searchable?: boolean;
    usePortalForMenu?: boolean;
    className?: string;

    onChange: (value: TValue) => void;

    hasError?: boolean;
};

export const DropDownControl = <TDataItem, TValue = unknown>(props: IDropDownControlProps<TDataItem, TValue>) => {
    const {
        data,
        textField,
        valueField,
        value,
        disabled,
        multiple,
        searchable,
        usePortalForMenu,
        className,
        onChange,
        hasError,
        ...dropDownProps
    } = props;

    const useItemFieldAccessor = (field: string | undefined) =>
        useCallback(
            (item: TDataItem) => {
                return item && field ? getIn(item, field) : item;
            },
            [field]
        );

    const getOptionLabel = useItemFieldAccessor(textField);
    const getOptionValue = useItemFieldAccessor(valueField);

    const currentValue = useMemo(() => {
        const areOptionsGrouped = data?.length && (data[0] as IGroupedOptions<TDataItem>).options;

        if (!valueField) return value as PropsValue<TDataItem>;
        const values = Array.isArray(value) ? (value as Array<TDataItem | TValue>) : [value as TDataItem | TValue];
        const isSelected = (item: TDataItem) => values.indexOf(getOptionValue(item)) >= 0;
        if (areOptionsGrouped) {
            const valueItems = [] as Array<TDataItem | TValue>;
            (data as IGroupedOptions<TDataItem>[]).forEach(groupedOptions => {
                groupedOptions.options.forEach(item => {
                    if (isSelected(item)) {
                        valueItems.push(item);
                    }
                });
            });
            return valueItems as PropsValue<TDataItem>;
        }
        return ((data as TDataItem[]) || []).filter(isSelected) as PropsValue<TDataItem>;
    }, [data, value, valueField, getOptionValue]);

    const classNames = cn(styles.dropDownControl, className, {
        multiple,
        'drop-down-control--has-error': !!hasError,
    });
    const menuPortalElement = document.querySelector('body');

    return (
        <Select<TDataItem, boolean>
            {...dropDownProps}
            className={classNames}
            classNamePrefix="drop-down"
            menuPortalTarget={usePortalForMenu ? menuPortalElement : undefined}
            options={data}
            value={currentValue as any}
            onChange={handleChange}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            menuPlacement="auto"
            closeMenuOnSelect={!multiple}
            isMulti={multiple}
            isSearchable={searchable}
            isDisabled={disabled}
            hideSelectedOptions={false}
            styles={{
                menuPortal: base => ({
                    ...base,
                    zIndex: MENU_PORTAL_ZINDEX,
                }),
            }}
        />
    );

    function handleChange(value: PropsValue<TDataItem>) {
        if (!valueField) {
            return (onChange as ChangeHandler<TDataItem | TDataItem[]>)(value as TDataItem | TDataItem[]);
        }

        if (!value) {
            const defaultValue = multiple ? [] : null;
            return (onChange as ChangeHandler<TValue | TValue[]>)(defaultValue as TValue | TValue[]);
        }

        const mappedValue = Array.isArray(value)
            ? value.map(item => getOptionValue(item))
            : getOptionValue(value as TDataItem);

        onChange(mappedValue);
    }
};
